/**
 * Global entity catalog — every entity TYPE the platform models, across all
 * foundry archetypes, in one searchable list.
 *
 * Today an "add equipment" picker only saw the 15 original grey-iron templates
 * (`ENTITY_TEMPLATES`), so a steel / aluminium / copper / zinc / investment
 * foundry couldn't find the kit it actually runs (EAF, AOD, crucible, reverb,
 * HPDC, gravity/LPDC, wax injection, shell build, dewax, cores, reclamation…).
 * Those defs already exist in the archetype `STAGE_LIBRARY`; this module unions
 * them with the originals (deduped by templateId) and precomputes a lowercase
 * search haystack (label + one-liner + class + material classes + parameter
 * names + QC tests/defects) so the picker can match by any of them.
 *
 * → "search shows the kinds of entities we have available globally, so it's
 *    easy to find a relevant entity and map it" (user ask, R3).
 */

import { ENTITY_TEMPLATES, type EntityDef } from "@foundry/foundry-plant";
import { STAGE_LIBRARY } from "./stages";

export interface CatalogEntry {
  templateId: string;
  /** Shop-floor display name (stage-library label, else prettified templateId). */
  label: string;
  def: EntityDef;
  /** Lowercased search haystack — label, one-liner, class, materials, params, QC. */
  search: string;
}

/** "foundry.induction_furnace" → "Induction Furnace". */
function labelFromTemplateId(def: EntityDef): string {
  const n = def.templateId.split(".").pop() ?? def.templateId;
  return n.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function entryFor(def: EntityDef, label: string): CatalogEntry {
  const ports = [...def.inputPorts, ...def.outputPorts];
  const materials = ports.map((p) => p.materialClass).filter(Boolean) as string[];
  const params = def.parameters.map((p) => p.name);
  const qc = def.trackedOutput?.qc;
  const search = [
    label,
    def.oneLine,
    def.class,
    def.lotOp,
    def.templateId.replace(/[._]/g, " "),
    ...materials,
    ...params,
    ...(qc?.tests ?? []),
    ...(qc?.defects ?? []),
  ]
    .join(" ")
    .toLowerCase();
  return { templateId: def.templateId, label, def, search };
}

/** All modelled entity types, deduped by templateId, alphabetised by label.
 *  Stage-library entries win (richer labels); any original template not in the
 *  stage library is appended with a prettified label. */
export const GLOBAL_ENTITY_CATALOG: CatalogEntry[] = (() => {
  const byId = new Map<string, CatalogEntry>();
  for (const s of Object.values(STAGE_LIBRARY)) {
    if (!byId.has(s.def.templateId)) byId.set(s.def.templateId, entryFor(s.def, s.label));
  }
  for (const def of Object.values(ENTITY_TEMPLATES)) {
    if (!byId.has(def.templateId)) byId.set(def.templateId, entryFor(def, labelFromTemplateId(def)));
  }
  return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
})();

/** templateId → EntityDef, for resolving a picked catalog entry. */
export const CATALOG_BY_ID: Record<string, EntityDef> = Object.fromEntries(
  GLOBAL_ENTITY_CATALOG.map((e) => [e.templateId, e.def]),
);

/** Filter the catalog by a free-text query (all whitespace-separated terms must
 *  match the entry's haystack). Empty query → the whole catalog. */
export function searchEntityCatalog(query: string): CatalogEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return GLOBAL_ENTITY_CATALOG;
  const terms = q.split(/\s+/);
  return GLOBAL_ENTITY_CATALOG.filter((e) => terms.every((t) => e.search.includes(t)));
}
